import { useState } from 'react';
import { usePage, router as Inertia } from '@inertiajs/react';

export function useRoute() {
  const { props } = usePage();
  const { locale } = props;

  return (pathWithQuery) => {
    const isDefaultLocale = locale === 'en';
    const [path, query] = pathWithQuery.split('?');

    if (path === '#') return path;

    const params = new URLSearchParams(query);

    if (!isDefaultLocale) params.set('locale', locale);

    const cleanedPath = path.replace(/^\/+/, '');
    const queryWithLocale = params.toString();

    return `/${cleanedPath}?${queryWithLocale}`;
  };
}

function useAction(method) {
  const route = useRoute();
  const [loading, setLoading] = useState(false);
  const { component } = usePage();
  const isActionWithoutData = method === 'visit' || method === 'delete';

  function action(path, data, options = {}) {
    const { preserveUrl, ...restOptions } = options;
    const originalHref = window.location.href;

    const restoreUrl = () => {
      window.history.replaceState({}, '', originalHref);
    };

    const shouldPreserveUrlOnRedirect = (page) => {
      if (!preserveUrl?.onSuccess) return false;
      const isRedirect = page.component !== component;
      return isRedirect ? preserveUrl.onRedirect !== false : true;
    };

    const enhancedOptions = {
      ...restOptions,
      preserveUrl: typeof preserveUrl === 'boolean' ? preserveUrl : false,
      onStart: () => {
        setLoading(true);
        options.onStart?.();
      },
      onSuccess: (page) => {
        if (shouldPreserveUrlOnRedirect(page)) {
          restoreUrl();
        }
        options.onSuccess?.(page);
      },
      onError: (errors) => {
        if (preserveUrl?.onError) {
          restoreUrl();
        }
        options.onError?.(errors);
      },
      onFinish: () => {
        setLoading(false);
        options.onFinish?.();
      },
    };

    return isActionWithoutData
      ? Inertia[method](route(path), enhancedOptions)
      : Inertia[method](route(path), data, enhancedOptions);
  }

  function actionWithoutData(path, options = {}) {
    action(path, null, options);
  }

  return isActionWithoutData ? [actionWithoutData, loading] : [action, loading];
}

export function useVisit() {
  return useAction('visit');
}

export function useGet() {
  return useAction('get');
}

export function usePost() {
  return useAction('post');
}

export function usePut() {
  return useAction('put');
}

export function usePatch() {
  return useAction('patch');
}

export function useDelete() {
  return useAction('delete');
}

export function useRouter() {
  const route = useRoute();

  const router = {
    visit: (path, options = {}) => Inertia.visit(route(path), options),
    get: (path, data, options = {}) => Inertia.get(route(path), data, options),
    post: (path, data, options = {}) =>
      Inertia.post(route(path), data, options),
    patch: (path, data, options = {}) =>
      Inertia.patch(route(path), data, options),
    put: (path, data, options = {}) => Inertia.put(route(path), data, options),
    delete: (path, options = {}) => Inertia.delete(route(path), options),
    reload: (options = {}) => Inertia.reload(options),
  };

  return { router, route };
}

export default useRouter;
